<template>
  <div class="ad-banners-admin" v-if="currentUser">
    <div class="page-header">
      <h1>Banery reklamowe</h1>
      <div class="header-actions">
        <router-link class="btn-primary" to="/admin-profile/ad-banners/create">
          + Dodaj baner
        </router-link>
      </div>
    </div>

    <section class="section-card list-card">
      <div class="section-card-header">
        <h2>Lista banerów</h2>

        <div class="filters-row">
          <select v-model="filters.target_group" @change="getBanners">
            <option value="">Wszystkie grupy</option>
            <option value="clients">Klienci</option>
            <option value="specialists">Specjaliści</option>
          </select>

          <select v-model="filters.is_active" @change="getBanners">
            <option value="">Wszystkie statusy</option>
            <option value="1">Aktywne</option>
            <option value="0">Nieaktywne</option>
          </select>

          <button class="btn-outline" type="button" @click="getBanners">Odśwież</button>
        </div>
      </div>

      <div v-if="isLoading" class="empty-state">
        <p>Ładowanie banerów...</p>
      </div>

      <div v-else-if="banners.length === 0" class="empty-state">
        <p>Brak banerów do wyświetlenia.</p>
      </div>

      <div v-else class="banners-list">
        <article v-for="banner in banners" :key="banner.id" class="banner-item">
          <div class="banner-item__main">
            <div>
              <h3>{{ banner.name }}</h3>
              <div class="chips">
                <span
                    v-for="group in banner.target_groups"
                    :key="group"
                    class="chip"
                >
                  {{ targetGroupLabel(group) }}
                </span>
                <span class="chip" :class="{ inactive: !banner.is_active }">
                  {{ banner.is_active ? 'Aktywny' : 'Nieaktywny' }}
                </span>
              </div>
            </div>

            <div class="banner-item__meta">
              <span>{{ banner.options && banner.options.width ? banner.options.width : '—' }} × {{ banner.options && banner.options.height ? banner.options.height : '—' }}</span>
              <span>Slajdy: {{ banner.slides ? banner.slides.length : 0 }}</span>
            </div>
          </div>

          <div class="banner-item__actions">
            <router-link class="btn-outline" :to="`/admin-profile/ad-banners/edit/${banner.id}`">
              Edytuj
            </router-link>
            <button class="btn-danger-outline" type="button" @click="deleteBanner(banner.id)">
              Usuń
            </button>
          </div>
        </article>
      </div>

      <div class="pagination">
        <button class="btn-outline" type="button" :disabled="page <= 1" @click="prevPage">
          &#8592; Poprzednia
        </button>
        <span>Strona {{ page }} / {{ totalPages }}</span>
        <button class="btn-outline" type="button" :disabled="page >= totalPages" @click="nextPage">
          Następna &#8594;
        </button>
      </div>
    </section>
  </div>
</template>

<script>
import axios from 'axios';
import authHeader from '@/services/auth-header';
import { useAuthStore } from '@/stores/auth';
import { useAppStore } from '@/stores/app';

export default {
  name: 'AdBannersAdmin',
  setup() {
    return {
      authStore: useAuthStore(),
      appStore: useAppStore(),
    };
  },
  data() {
    return {
      banners: [],
      filters: {
        target_group: '',
        is_active: '',
      },
      page: 1,
      totalPages: 1,
      isLoading: false,
    };
  },
  computed: {
    currentUser() {
      return this.authStore.user;
    },
  },
  mounted() {
    if (!this.currentUser) {
      this.$router.push('/login');
      return;
    }

    if (this.currentUser.role_id !== 1) {
      this.$router.push('/login');
      return;
    }

    this.getBanners();
  },
  methods: {
    getBanners() {
      this.isLoading = true;

      const params = {
        page: this.page,
        limit: 20,
      };

      if (this.filters.target_group) {
        params.target_group = this.filters.target_group;
      }

      if (this.filters.is_active !== '') {
        params.is_active = this.filters.is_active;
      }

      axios.get('/admin/ad-banners', {
        params,
        ...authHeader(),
      }).then(res => {
        this.banners = Array.isArray(res.data) ? res.data : (res.data.data || []);
        this.totalPages = res.data.meta && res.data.meta.last_page ? res.data.meta.last_page : 1;
      }).catch(() => {
        this.banners = [];
        this.appStore.setIsAlert('Nie udało się pobrać banerów');
      }).finally(() => {
        this.isLoading = false;
      });
    },
    deleteBanner(id) {
      const answer = prompt('Czy na pewno chcesz usunąć baner? Wpisz "tak", jeśli tak.');

      if (answer !== 'tak') {
        return;
      }

      axios.delete(`/admin/ad-banners/${id}`, authHeader()).then(() => {
        this.appStore.setIsSuccess('Baner został usunięty');
        this.getBanners();
      }).catch(() => {
        this.appStore.setIsAlert('Nie udało się usunąć banera');
      });
    },
    targetGroupLabel(group) {
      if (group === 'clients') {
        return 'Klienci';
      }

      if (group === 'specialists') {
        return 'Specjaliści';
      }

      return group;
    },
    prevPage() {
      if (this.page > 1) {
        this.page -= 1;
        this.getBanners();
      }
    },
    nextPage() {
      if (this.page < this.totalPages) {
        this.page += 1;
        this.getBanners();
      }
    },
  },
};
</script>

<style scoped lang="scss">
.ad-banners-admin {
  max-width: 1400px;
  margin: 0 auto;
  padding: 32px 16px;
  font-family: sans-serif;
  box-sizing: border-box;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #e5e7eb;
}

.page-header h1 {
  font-size: 2.8rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.section-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, .08);
  padding: 24px;
  box-sizing: border-box;
  width: 100%;
}

.section-card-header {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 18px;
}

.section-card h2 {
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.filters-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.filters-row select {
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 9px 11px;
  font-size: 1.4rem;
  outline: none;
  background: #fff;
}

.filters-row select:focus {
  border-color: #10b981;
}

.banners-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.banner-item {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 14px;
}

.banner-item__main {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.banner-item h3 {
  margin: 0 0 8px;
  color: #1f2937;
  font-size: 1.6rem;
}

.banner-item__meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  color: #6b7280;
  font-size: 1.35rem;
  text-align: right;
}

.chips {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.chip {
  display: inline-flex;
  border-radius: 999px;
  background: #ecfdf5;
  color: #047857;
  padding: 4px 9px;
  font-size: 1.2rem;
  font-weight: 700;
}

.chip.inactive {
  background: #fee2e2;
  color: #991b1b;
}

.banner-item__actions {
  display: flex;
  gap: 8px;
  margin-top: 12px;
  flex-wrap: wrap;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
  color: #6b7280;
  font-size: 1.4rem;
}

.empty-state {
  text-align: center;
  padding: 26px;
  color: #9ca3af;
  font-size: 1.5rem;
  border: 1px dashed #d1d5db;
  border-radius: 10px;
}

.btn-primary,
.btn-outline,
.btn-danger-outline {
  border-radius: 8px;
  padding: 9px 15px;
  cursor: pointer;
  font-size: 1.4rem;
  font-weight: 600;
  text-decoration: none;
  display: inline-block;
  text-align: center;
}

.btn-primary {
  background: #f97316;
  color: #fff;
  border: none;
}

.btn-primary:hover {
  background: #ea6c0a;
}

.btn-outline {
  background: #fff;
  color: #1f2937;
  border: 1px solid #d1d5db;
}

.btn-outline:hover {
  border-color: #10b981;
  color: #10b981;
}

.btn-danger-outline {
  background: #fff;
  color: #991b1b;
  border: 1px solid #fecaca;
}

.btn-danger-outline:hover {
  background: #fee2e2;
}

button:disabled {
  opacity: .55;
  cursor: not-allowed;
}

@media (max-width: 640px) {
  .ad-banners-admin {
    padding: 16px 12px;
  }

  .page-header h1 {
    font-size: 2.2rem;
  }

  .section-card {
    padding: 16px 12px;
  }

  .header-actions,
  .banner-item__main {
    flex-direction: column;
  }

  .btn-primary,
  .btn-outline,
  .btn-danger-outline {
    width: 100%;
    text-align: center;
  }

  .banner-item__meta {
    text-align: left;
  }
}
</style>